import Vue from 'vue'
import util from '@/util'
import { any, count, find } from '@/store/helpers/getters'

const CONTENT_URL_PENDING = 'pending'
const CONTENT_URL_OK = 'ok'
const CONTENT_URL_ERROR = 'error'

/**
 * HTTP status for each content URL. Reactive so the banner and icon
 * update after the request finishes.
 *
 * @type {Object}
 */
const contentUrlStatuses = Vue.observable({})

/**
 * A slot can be considered only when its flag is on and it has a URL.
 * Missing flags default to false and missing URLs default to null.
 *
 * @param {Boolean} isEnabled
 * @param {String|null} url
 * @return {Boolean}
 */
function hasEnabledContentUrl(isEnabled = false, url = null) {
	return Boolean(isEnabled) && Boolean(url && String(url).trim())
}

/**
 * Canonical content URL, or null when the flag is off or the URL is empty.
 *
 * @param {Boolean} isEnabled
 * @param {String|null} url
 * @return {String|null}
 */
function resolvedContentUrl(isEnabled = false, url = null) {
	if (!hasEnabledContentUrl(isEnabled, url)) {
		return null
	}

	return util.normalizeContentSlotUrl(url) || null
}

/**
 * Request the content URL once. The slot stays hidden until the
 * response status is exactly 200.
 *
 * @param {String} contentUrl
 * @return {void}
 */
function ensureContentUrlChecked(contentUrl) {
	if (contentUrlStatuses[contentUrl]) {
		return
	}

	Vue.set(contentUrlStatuses, contentUrl, CONTENT_URL_PENDING)

	fetch(contentUrl)
		.then(response => {
			Vue.set(
				contentUrlStatuses,
				contentUrl,
				response.status === 200 ? CONTENT_URL_OK : CONTENT_URL_ERROR
			)
		})
		.catch(() => {
			Vue.set(contentUrlStatuses, contentUrl, CONTENT_URL_ERROR)
		})
}

/**
 * Read a partner enable flag and URL, applying the defaults.
 *
 * @param {Object|null} partner
 * @param {String} enableField
 * @param {String} urlField
 * @return {{ isEnabled: Boolean, url: String|null }}
 */
function partnerContentFields(partner, enableField, urlField) {
	return {
		isEnabled: partner?.[enableField] ?? false,
		url: partner?.[urlField] ?? null,
	}
}

/**
 * Overview banner fields: enable_calls_content_card and calls_content_card.
 *
 * @param {Object|null} partner
 * @return {{ isEnabled: Boolean, url: String|null }}
 */
function callsContentCardSettings(partner) {
	return partnerContentFields(
		partner,
		'enable_calls_content_card',
		'calls_content_card'
	)
}

/**
 * Clarus AI icon fields: enable_cai_app_icon and ai_app_icon.
 *
 * @param {Object|null} partner
 * @return {{ isEnabled: Boolean, url: String|null }}
 */
function aiAppIconSettings(partner) {
	return partnerContentFields(
		partner,
		'enable_cai_app_icon',
		'ai_app_icon'
	)
}

/**
 * Current check state for a content URL: unavailable, pending, ok, or error.
 *
 * @param {Boolean} isEnabled
 * @param {String|null} url
 * @return {String}
 */
function contentUrlCheckStatus(isEnabled, url) {
	const contentUrl = resolvedContentUrl(isEnabled, url)

	if (!contentUrl) {
		return 'unavailable'
	}

	ensureContentUrlChecked(contentUrl)

	return contentUrlStatuses[contentUrl]
}

/**
 * Whether the slot may be shown. True only after a 200 response.
 *
 * @param {Boolean} isEnabled
 * @param {String|null} url
 * @return {Boolean}
 */
function isEnabledContentUrlReady(isEnabled, url) {
	return contentUrlCheckStatus(isEnabled, url) === CONTENT_URL_OK
}

/**
 * Content URL to load in the slot, or null until the check returns 200.
 *
 * @param {Boolean} isEnabled
 * @param {String|null} url
 * @return {String|null}
 */
function getEnabledContentUrl(isEnabled = false, url = null) {
	const contentUrl = resolvedContentUrl(isEnabled, url)

	if (!contentUrl || contentUrlCheckStatus(isEnabled, url) !== CONTENT_URL_OK) {
		return null
	}

	return contentUrl
}

/**
 * The module's reactive state getters.
 *
 * @author Erik Galloway <egalloway@claruscare.com>
 */
export default {
	any,
	count,
	find,

	/**
	 * Get the active partner.
	 *
	 * @param {Object} state
	 * @return {Object}
	 */
	active(state) {
		return state.items[state.active] || null
	},

	/**
	 * Find a linked partner by id.
	 *
	 * @param {Object} state
	 * @param {Function} getters
	 * @return {Object}
	 */
	findLinkedPartner(state, getters) {
		return linkedPartnerId => {
			let linkedPartner = {}
			const active = getters.active

			if (active && Array.isArray(active?.linked_partners)) {
				linkedPartner = active.linked_partners.find(
					linkedPartner => linkedPartner.id === linkedPartnerId
				)
			}

			return linkedPartner
		}
	},

	/**
	 * Find a provider by id.
	 *
	 * @param {Object} state
	 * @param {Function} getters
	 * @return {Object}
	 */
	findProvider(state, getters) {
		return providerId => {
			const active = getters.active

			const provider = active?.providers.find(
				provider => provider.id === providerId
			)

			return provider
		}
	},

	/**
	 * Find a provider group by id.
	 *
	 * @param {Object} state
	 * @param {Function} getters
	 * @return {Object}
	 */
	findProviderGroup(state, getters) {
		return providerGroupId => {
			const active = getters.active

			const providerGroup =
				active?.provider_groups?.find(
					providerGroup => providerGroup.id === providerGroupId
				) || false

			return providerGroup
		}
	},

	/**
	 * Get the active partner's calendars.
	 *
	 * @param {Object} state
	 * @return {Object}
	 */
	getCalendars(state) {
		return id => {
			return state.items[id].calendars || null
		}
	},

	/**
	 * Get the active partner's linkedPartners.
	 *
	 * @param {Object} state
	 * @return {Object}
	 */
	getLinkedPartners(state) {
		return id => {
			return state.items[id].linked_partners || null
		}
	},

	/**
	 * Get the active partner's providerGroups.
	 *
	 * @param {Object} state
	 * @return {Object}
	 */
	getProviderGroups(state) {
		return id => {
			return state.items[id].provider_groups || null
		}
	},

	/**
	 * Determine if the active partner has cover my call enabled.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @return {Boolean}
	 */
	hasCoverMyCallEnabled(state, getters) {
		return !!(getters.active && getters.active.enable_cover_my_call)
	},

	/**
	 * Determine if the active partner has paging enabled.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @return {Boolean}
	 */
	hasPagingEnabled(state, getters) {
		return !!(getters.active && getters.active.enable_paging)
	},

	/**
	 * Whether the calls overview banner can be shown.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @return {Boolean}
	 */
	hasCallsContentCard(state, getters) {
		const { isEnabled, url } = callsContentCardSettings(getters.active)

		return isEnabledContentUrlReady(isEnabled, url)
	},

	/**
	 * Calls overview banner URL.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @return {String|null}
	 */
	callsContentCardUrl(state, getters) {
		const { isEnabled, url } = callsContentCardSettings(getters.active)

		return getEnabledContentUrl(isEnabled, url)
	},

	/**
	 * Whether the Clarus AI sidebar icon can be shown.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @return {Boolean}
	 */
	hasAiAppIcon(state, getters) {
		const { isEnabled, url } = aiAppIconSettings(getters.active)

		return isEnabledContentUrlReady(isEnabled, url)
	},

	/**
	 * Clarus AI app URL.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @return {String|null}
	 */
	aiAppIconUrl(state, getters) {
		const { isEnabled, url } = aiAppIconSettings(getters.active)

		return getEnabledContentUrl(isEnabled, url)
	},

	/**
	 * Whether the Clarus AI URL check is still in flight.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @return {Boolean}
	 */
	isAiAppIconUrlPending(state, getters) {
		const { isEnabled, url } = aiAppIconSettings(getters.active)

		return contentUrlCheckStatus(isEnabled, url) === CONTENT_URL_PENDING
	},

	clarusAppType(state, getters) {
		const active = getters.active
		if (!active?.applications) {
			return false
		}
		return active.applications.some(
			app => app.name === 'daytime' || app.name === 'afterhours'
		)
	},

	isEngageApp(state, getters, rootState) {
		const active = getters.active
		if (!active?.applications) {
			return false
		}
		const isUserHasAccess = rootState?.auth?.user?.permissions?.some(
			permission => permission?.toLowerCase() === 'engage'
		)
		const engage = active.applications.find(app => app.name === 'engage')

		return engage && isUserHasAccess ? true : false
	},

	engageOnlyAppWithoutAccess(state, getters, rootState) {
		const active = getters.active
		if (!active?.applications) {
			return false
		}

		const permissions = rootState?.auth?.user?.permissions || []
		const hasNoPermissions = !permissions?.some(
			p => p?.toLowerCase() === 'engage'
		)

		const hasEngage = active.applications.some(app => app.name === 'engage')

		const hasOtherApps = active.applications.some(
			app => app.name !== 'engage'
		)

		return hasEngage && hasNoPermissions && !hasOtherApps
	},
}
